if (!customElements.get("scrolling-text-banner")) {
  customElements.define(
    "scrolling-text-banner",

    class ScrollingTextBanner extends HTMLElement {
      constructor() {
        super();
        this.spacing = 20; // Default spacing between phrases
        this.speedFactor = 10; // Base speed multiplier
        this.speed = 100; // Initial speed (calculated later)
        this.mobileBreakpoint = 600; // Width threshold for mobile devices
        this.mobileSpeedFactor = 0.7; // Default factor for mobile speed adjustment
        this.resizeObserver = null; // For observing size changes
      }

      connectedCallback() {
        // Read spacing and speed from data attributes
        this.spacing =
          parseInt(this.getAttribute("data-spacing")) || this.spacing;

        // Read speed in range 1-10 and map to actual speed
        const speedInput = parseInt(this.getAttribute("data-speed")) || 5; // Default to 5
        this.speed = this.mapSpeed(speedInput);

        // Read mobile speed factor from data attribute
        this.mobileSpeedFactor =
          parseFloat(this.getAttribute("data-mobile-speed-factor")) ||
          this.mobileSpeedFactor;

        // Initialize the scrolling text
        this.initializeScrollingText();

        // Add resize listener
        this.observeResizing();
      }

      disconnectedCallback() {
        // Clean up resize observer if the component is removed
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
      }

      mapSpeed(input) {
        // Map input range [1, 10] to speed in pixels per second
        const minSpeed = 30; // Slowest speed in pixels per second
        const maxSpeed = 300; // Fastest speed in pixels per second
        return minSpeed + (maxSpeed - minSpeed) * ((input - 1) / 9);
      }

      getAdjustedSpeed() {
        // Adjust speed based on the mobile breakpoint
        if (window.innerWidth < this.mobileBreakpoint) {
          return this.speed * this.mobileSpeedFactor;
        }
        return this.speed;
      }

      initializeScrollingText() {
        // Find ALL `.hero__title` elements inside the component
        const heroTitles = this.querySelectorAll(".hero__title");
        if (!heroTitles.length) {
          console.error(
            "No `.hero__title` elements found inside the scrolling-text-banner component.",
          );
          return;
        }

        // Collect full HTML content (so images + text are kept) from all hero titles
        const allContentHTML = [];
        heroTitles.forEach((heroTitle) => {
          const contentHTML = heroTitle.innerHTML.trim();
          if (contentHTML) {
            allContentHTML.push(contentHTML);
          }
        });

        if (allContentHTML.length === 0) {
          console.error(
            "No content found in any `.hero__title` elements for scrolling-text-banner.",
          );
          return;
        }

        // Clear all hero titles and use only the first one for the scrolling container
        heroTitles.forEach((heroTitle, index) => {
          if (index === 0) {
            // Use the first hero title as the main container
            heroTitle.innerHTML = "";

            // Create a container for the scrolling wrappers
            const scrollingContainer = document.createElement("div");
            scrollingContainer.classList.add("scrolling-container");
            heroTitle.appendChild(scrollingContainer);

            // Create two wrappers for seamless animation
            for (let i = 0; i < 2; i++) {
              const wrapper = document.createElement("div");
              wrapper.classList.add("scrolling-wrapper");

              // Create and append elements for each block content (HTML, including images)
              for (let j = 0; j < 10; j++) {
                allContentHTML.forEach((contentHTML) => {
                  const textWrapper = document.createElement("div");
                  textWrapper.classList.add("scrolling-text");
                  textWrapper.innerHTML = contentHTML;
                  textWrapper.style.marginRight = `${this.spacing}px`; // Apply spacing
                  wrapper.appendChild(textWrapper);
                });
              }

              // Append the wrapper to the container
              scrollingContainer.appendChild(wrapper);
            }

            // Set animation speed dynamically for this container
            this.setAnimationSpeed(scrollingContainer);
          } else {
            // Hide the other hero titles since we're using only the first one
            heroTitle.style.display = "none";
          }
        });
      }

      setAnimationSpeed(scrollingContainer) {
        // Wait for layout to calculate sizes
        requestAnimationFrame(() => {
          const wrappers =
            scrollingContainer.querySelectorAll(".scrolling-wrapper");
          if (wrappers.length !== 2) return;

          const wrapperWidth = wrappers[0].offsetWidth; // Dynamic width of the first wrapper
          const adjustedSpeed = this.getAdjustedSpeed(); // Adjust speed for mobile
          const duration = wrapperWidth / adjustedSpeed; // Use pre-calculated speed

          // Apply calculated width and animation styles
          scrollingContainer.style.width = `${wrapperWidth * 2}px`; // Allow two wrappers to fit seamlessly
          wrappers.forEach((wrapper, index) => {
            wrapper.style.animationDuration = `${duration}s`;
            wrapper.style.animationDelay = `${-index * duration}s`; // Offset the second wrapper
          });
        });
      }

      observeResizing() {
        // Observe only the first `.hero__title` element since we're using it as the main container
        const heroTitle = this.querySelector(".hero__title");
        if (!heroTitle) return;

        this.resizeObserver = new ResizeObserver(() => {
          this.recalculateAnimation();
        });
        
        this.resizeObserver.observe(heroTitle);
      }

      recalculateAnimation() {
        const scrollingContainers = this.querySelectorAll(".scrolling-container");
        scrollingContainers.forEach(scrollingContainer => {
          this.setAnimationSpeed(scrollingContainer);
        });
      }
    },
  );
}
